const LANDMARKS = {
  NOSE_TIP: 1,
  LEFT_EYE_OUTER: 33,
  RIGHT_EYE_OUTER: 263,
  FOREHEAD_TOP: 10,
  CHIN_BOTTOM: 152,
};

const CENTER_TOLERANCE_X = 0.14;
const CENTER_TOLERANCE_Y = 0.18;
const MIN_FACE_WIDTH = 0.2;
const MAX_FACE_WIDTH = 0.72;
const FRONT_YAW_THRESHOLD = 0.03;
const SIDE_YAW_THRESHOLD = 0.045;
const TOP_PITCH_THRESHOLD = 0.23;
const TOP_DEPTH_THRESHOLD = 0.08;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pickLandmark = (landmarks, index, fallbackIndex) => {
  if (landmarks?.[index]) return landmarks[index];
  if (fallbackIndex !== undefined && landmarks?.[fallbackIndex]) return landmarks[fallbackIndex];
  return null;
};

const getFaceBounds = (landmarks) => {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;

  for (let i = 0; i < landmarks.length; i += 1) {
    const point = landmarks[i];
    if (!point) continue;
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return {
    minX: clamp(minX, 0, 1),
    minY: clamp(minY, 0, 1),
    maxX: clamp(maxX, 0, 1),
    maxY: clamp(maxY, 0, 1),
    width: clamp(maxX - minX, 0, 1),
    height: clamp(maxY - minY, 0, 1),
    centerX: clamp((minX + maxX) / 2, 0, 1),
    centerY: clamp((minY + maxY) / 2, 0, 1),
  };
};

const getYawScore = (landmarks, bounds) => {
  const leftEye = pickLandmark(landmarks, LANDMARKS.LEFT_EYE_OUTER);
  const rightEye = pickLandmark(landmarks, LANDMARKS.RIGHT_EYE_OUTER);
  const nose = pickLandmark(landmarks, LANDMARKS.NOSE_TIP, 4);

  if (!leftEye || !rightEye || !nose || bounds.width <= 0.0001) {
    return 0;
  }

  const eyeMidX = (leftEye.x + rightEye.x) / 2;
  return (nose.x - eyeMidX) / bounds.width;
};

const getDirectionFromYaw = (yawScore) => {
  if (yawScore > SIDE_YAW_THRESHOLD) return 'left';
  if (yawScore < -SIDE_YAW_THRESHOLD) return 'right';
  return 'front';
};

const getTopPoseScore = (landmarks, bounds) => {
  const leftEye = pickLandmark(landmarks, LANDMARKS.LEFT_EYE_OUTER);
  const rightEye = pickLandmark(landmarks, LANDMARKS.RIGHT_EYE_OUTER);
  const nose = pickLandmark(landmarks, LANDMARKS.NOSE_TIP, 4);
  const forehead = pickLandmark(landmarks, LANDMARKS.FOREHEAD_TOP, 151);
  const chin = pickLandmark(landmarks, LANDMARKS.CHIN_BOTTOM);

  if (!leftEye || !rightEye || !nose || !forehead || !chin || bounds.height <= 0.0001) {
    return {
      pitchScore: 0,
      depthScore: 0,
    };
  }

  const eyeMidY = (leftEye.y + rightEye.y) / 2;
  return {
    pitchScore: (nose.y - eyeMidY) / bounds.height,
    depthScore: chin.z - forehead.z,
  };
};

const matchesExpectedDirection = (expectedDirection, yawScore, topPoseScore) => {
  if (expectedDirection === 'custom') return true;
  if (expectedDirection === 'front') return Math.abs(yawScore) <= FRONT_YAW_THRESHOLD;
  if (expectedDirection === 'left') return yawScore > SIDE_YAW_THRESHOLD;
  if (expectedDirection === 'right') return yawScore < -SIDE_YAW_THRESHOLD;
  if (expectedDirection === 'top') {
    return (
      topPoseScore.pitchScore >= TOP_PITCH_THRESHOLD &&
      topPoseScore.depthScore >= TOP_DEPTH_THRESHOLD
    );
  }
  return true;
};

export const validateFaceForStep = ({ expectedDirection = 'front', multiFaceLandmarks = [] }) => {
  if (!Array.isArray(multiFaceLandmarks) || multiFaceLandmarks.length === 0) {
    return {
      isValid: false,
      guidance: 'Face Not Detected',
      faceCount: 0,
      direction: 'unknown',
      bounds: null,
      yawScore: 0,
    };
  }

  if (multiFaceLandmarks.length > 1) {
    return {
      isValid: false,
      guidance: 'Multiple Faces Detected',
      faceCount: multiFaceLandmarks.length,
      direction: 'unknown',
      bounds: null,
      yawScore: 0,
    };
  }

  const landmarks = multiFaceLandmarks[0];
  const bounds = getFaceBounds(landmarks);
  const yawScore = getYawScore(landmarks, bounds);
  const topPoseScore = getTopPoseScore(landmarks, bounds);
  const direction = getDirectionFromYaw(yawScore);

  if (bounds.width < MIN_FACE_WIDTH) {
    return {
      isValid: false,
      guidance: 'Move Closer',
      faceCount: 1,
      direction,
      bounds,
      yawScore,
    };
  }

  if (bounds.width > MAX_FACE_WIDTH) {
    return {
      isValid: false,
      guidance: 'Move Back Slightly',
      faceCount: 1,
      direction,
      bounds,
      yawScore,
    };
  }

  const isCentered =
    Math.abs(bounds.centerX - 0.5) <= CENTER_TOLERANCE_X &&
    Math.abs(bounds.centerY - 0.5) <= CENTER_TOLERANCE_Y;

  if (!isCentered) {
    return {
      isValid: false,
      guidance: 'Center Your Face',
      faceCount: 1,
      direction,
      bounds,
      yawScore,
    };
  }

  if (!matchesExpectedDirection(expectedDirection, yawScore, topPoseScore)) {
    if (expectedDirection === 'front') {
      return {
        isValid: false,
        guidance: 'Look Straight',
        faceCount: 1,
        direction,
        bounds,
        yawScore,
      };
    }

    if (expectedDirection === 'left') {
      return {
        isValid: false,
        guidance: 'Turn Left',
        faceCount: 1,
        direction,
        bounds,
        yawScore,
      };
    }

    if (expectedDirection === 'right') {
      return {
        isValid: false,
        guidance: 'Turn Right',
        faceCount: 1,
        direction,
        bounds,
        yawScore,
      };
    }

    if (expectedDirection === 'top') {
      return {
        isValid: false,
        guidance: 'Tilt Head Down',
        faceCount: 1,
        direction,
        bounds,
        yawScore,
      };
    }
  }

  return {
    isValid: true,
    guidance: 'Hold Still',
    faceCount: 1,
    direction,
    bounds,
    yawScore,
  };
};

export const getDefaultStepPrompt = (direction) => {
  if (direction === 'left') return 'Turn Left';
  if (direction === 'right') return 'Turn Right';
  if (direction === 'top') return 'Tilt Head Down';
  if (direction === 'custom') return 'Custom Angle';
  return 'Look Straight';
};
